import React, { useState, useEffect, useMemo } from "react";
import { GamificationService } from "../../services/gamificationService";
import { LeaderboardEntry } from "../../types/education";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Trophy,
  Users,
  TrendingUp,
  Calendar,
  Crown,
  Medal,
  Award,
  Star,
} from "lucide-react";

interface LeaderboardProps {
  className?: string;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ className }) => {
  const [activeTab, setActiveTab] = useState<"points" | "streaks" | "weekly">(
    "points",
  );
  const [leaderboardData, setLeaderboardData] = useState<
    Record<string, LeaderboardEntry[]>
  >({});
  const [loading, setLoading] = useState(true);

  const gamificationService = useMemo(() => new GamificationService(), []);

  useEffect(() => {
    const loadLeaderboardData = async () => {
      setLoading(true);
      try {
        const data = {
          points: gamificationService.getLeaderboard("points"),
          streaks: gamificationService.getLeaderboard("streaks"),
          weekly: gamificationService.getLeaderboard("weekly"),
        };
        setLeaderboardData(data);
      } catch (error) {
        console.error("Error loading leaderboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboardData();
  }, []);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Award className="h-5 w-5 text-orange-600" />;
      default:
        return (
          <span className="text-lg font-bold text-muted-foreground">
            #{rank}
          </span>
        );
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return <Badge className="bg-yellow-500">1st</Badge>;
    if (rank === 2) return <Badge className="bg-gray-400">2nd</Badge>;
    if (rank === 3) return <Badge className="bg-orange-600">3rd</Badge>;
    return <Badge variant="outline">#{rank}</Badge>;
  };

  const LeaderboardTable: React.FC<{
    entries: LeaderboardEntry[];
    type: string;
  }> = ({ entries, type }) => {
    const getScoreLabel = () => {
      switch (type) {
        case "points":
          return "Points";
        case "streaks":
          return "Day Streak";
        case "weekly":
          return "Weekly Score";
        default:
          return "Score";
      }
    };

    const getScoreIcon = () => {
      switch (type) {
        case "points":
          return <Star className="h-4 w-4" />;
        case "streaks":
          return <TrendingUp className="h-4 w-4" />;
        case "weekly":
          return <Calendar className="h-4 w-4" />;
        default:
          return <Trophy className="h-4 w-4" />;
      }
    };

    if (loading) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!entries || entries.length === 0) {
      return (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">No data available</h3>
          <p className="text-muted-foreground">
            Leaderboard data will appear once users start competing.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div
            key={entry.userId}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              index === 0
                ? "bg-yellow-50 border-yellow-200"
                : index === 1
                  ? "bg-gray-50 border-gray-200"
                  : index === 2
                    ? "bg-orange-50 border-orange-200"
                    : "hover:bg-muted/50"
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8">
                {getRankIcon(entry.rank)}
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-xl">{entry.avatar}</div>
                <div>
                  <div className="font-medium">{entry.username}</div>
                  <div className="text-sm text-muted-foreground">
                    Level {Math.floor(entry.score / 100) + 1}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {getScoreIcon()}
              <span className="font-bold text-lg">
                {entry.score.toLocaleString()}
              </span>
              <span className="text-sm text-muted-foreground">
                {getScoreLabel()}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Trophy className="h-5 w-5 mr-2" />
          Leaderboard
        </CardTitle>
        <CardDescription>
          See how you rank against other learners
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as any)}
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="points" className="flex items-center space-x-2">
              <Star className="h-4 w-4" />
              <span>Points</span>
            </TabsTrigger>
            <TabsTrigger
              value="streaks"
              className="flex items-center space-x-2"
            >
              <TrendingUp className="h-4 w-4" />
              <span>Streaks</span>
            </TabsTrigger>
            <TabsTrigger value="weekly" className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Weekly</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="points" className="mt-4">
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Top learners by total achievement points
              </div>
              <LeaderboardTable
                entries={leaderboardData.points || []}
                type="points"
              />
            </div>
          </TabsContent>

          <TabsContent value="streaks" className="mt-4">
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Longest learning streaks this month
              </div>
              <LeaderboardTable
                entries={leaderboardData.streaks || []}
                type="streaks"
              />
            </div>
          </TabsContent>

          <TabsContent value="weekly" className="mt-4">
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Top performers this week
              </div>
              <LeaderboardTable
                entries={leaderboardData.weekly || []}
                type="weekly"
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

interface MiniLeaderboardProps {
  type: "points" | "streaks";
  limit?: number;
  className?: string;
}

export const MiniLeaderboard: React.FC<MiniLeaderboardProps> = ({
  type,
  limit = 5,
  className,
}) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const gamificationService = useMemo(() => new GamificationService(), []);

  useEffect(() => {
    const loadMiniLeaderboard = async () => {
      setLoading(true);
      try {
        const data = gamificationService.getLeaderboard(type);
        setEntries(data.slice(0, limit));
      } catch (error) {
        console.error("Error loading mini leaderboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMiniLeaderboard();
  }, [type, limit]);

  const getTitle = () => {
    switch (type) {
      case "points":
        return "Top Points";
      case "streaks":
        return "Top Streaks";
      default:
        return "Leaderboard";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "points":
        return <Star className="h-4 w-4" />;
      case "streaks":
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Trophy className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center text-sm">
            {getIcon()}
            <span className="ml-2">{getTitle()}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(limit)].map((_, i) => (
              <div key={i} className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-muted animate-pulse rounded"></div>
                <div className="flex-1 h-4 bg-muted animate-pulse rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center text-sm">
          {getIcon()}
          <span className="ml-2">{getTitle()}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.userId}
              className="flex items-center justify-between"
            >
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-muted-foreground w-4">
                  {entry.rank}.
                </span>
                <span className="text-sm">{entry.avatar}</span>
                <span className="text-sm font-medium truncate max-w-20">
                  {entry.username}
                </span>
              </div>
              <span className="text-sm font-bold">
                {entry.score.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
